import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { TreasureItem } from '../../types';
import { useReferenceDataStore } from '../../stores/referenceDataStore';

type ItemCategory = 'general' | 'scrolls' | 'potions' | 'grimoires';

const CATEGORIES: { key: ItemCategory; label: string }[] = [
  { key: 'general', label: 'Magic Items' },
  { key: 'scrolls', label: 'Scrolls' },
  { key: 'potions', label: 'Potions' },
  { key: 'grimoires', label: 'Grimoires' },
];

function categoriseItem(item: TreasureItem): ItemCategory {
  const id = item.id.toLowerCase();
  if (id.startsWith('scroll_')) return 'scrolls';
  if (id.startsWith('grimoire_')) return 'grimoires';
  if (item.baseItemType === 'potion') return 'potions';
  return 'general';
}

interface SelectCreatureTreasureItemModalProps {
  open: boolean;
  excludeItemIds: string[];
  onClose: () => void;
  onSelect: (item: TreasureItem) => void;
}

export const SelectCreatureTreasureItemModal: React.FC<SelectCreatureTreasureItemModalProps> = ({
  open,
  excludeItemIds,
  onClose,
  onSelect,
}) => {
  const allItems = useReferenceDataStore((s) => s.treasureItems);

  const [activeTab, setActiveTab] = React.useState(0);
  const [filter, setFilter] = React.useState('');
  const [selected, setSelected] = React.useState<TreasureItem | null>(null);

  React.useEffect(() => {
    if (!open) {
      setSelected(null);
      setFilter('');
      setActiveTab(0);
    }
  }, [open]);

  const categorised = React.useMemo(() => {
    const lower = filter.trim().toLowerCase();
    const available = allItems.filter((i) => !excludeItemIds.includes(i.id));
    const filtered = lower
      ? available.filter(
          (i) =>
            i.name.toLowerCase().includes(lower) ||
            (i.description?.toLowerCase().includes(lower) ?? false)
        )
      : available;

    const groups: Record<ItemCategory, TreasureItem[]> = {
      general: [],
      scrolls: [],
      potions: [],
      grimoires: [],
    };
    for (const item of filtered) {
      groups[categoriseItem(item)].push(item);
    }
    for (const key of Object.keys(groups) as ItemCategory[]) {
      groups[key].sort((a, b) => a.name.localeCompare(b.name));
    }
    return groups;
  }, [allItems, excludeItemIds, filter]);

  const activeCategory = CATEGORIES[activeTab]?.key ?? 'general';
  const displayed = categorised[activeCategory];

  const handleConfirm = () => {
    if (selected) {
      onSelect(selected);
      setSelected(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Tabs
        value={activeTab}
        onChange={(_, v) => { setActiveTab(v as number); setSelected(null); }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}
      >
        {CATEGORIES.map((c) => (
          <Tab key={c.key} label={`${c.label} (${categorised[c.key].length})`} />
        ))}
      </Tabs>
      <DialogContent dividers sx={{ pt: 1 }}>
        <TextField
          size="small"
          placeholder="Search…"
          value={filter}
          onChange={(e) => { setFilter(e.target.value); setSelected(null); }}
          fullWidth
          sx={{ mb: 1.5 }}
        />
        {displayed.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No items found.
          </Typography>
        ) : (
          <List disablePadding>
            {displayed.map((item) => (
              <ListItemButton
                key={item.id}
                selected={selected?.id === item.id}
                onClick={() => setSelected(item)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body1" fontWeight={selected?.id === item.id ? 600 : 400}>
                      {item.name}
                    </Typography>
                  }
                  secondary={
                    item.description && (
                      <Typography variant="caption" color="text.secondary" component="span">
                        {item.description}
                      </Typography>
                    )
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!selected} onClick={handleConfirm}>
          Add Item
        </Button>
      </DialogActions>
    </Dialog>
  );
};
